import sendRequest from "@/libs/sendRequest";

export const precios = {
  namespaced: true,

  state: {
    // Precios
    data: [],
    total_data: 0,
    loading: false,
    error: null,

    // Rutas
    rutas: {
      data: [],
      loading: false,
      error: null
    },

    // Clientes
    clients: {
      data: [],
      total: 0,
      loading: false,
      error: null
    }
  },

  getters: {
    // Precios
    getItems: (state) => state.data,
    getTotal: (state) => state.total_data,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,

    // Rutas
    getRutas: (state) => state.rutas.data,
    isRutasLoading: (state) => state.rutas.loading,
    getRutasError: (state) => state.rutas.error,

    // Clientes
    getClients: (state) => state.clients.data,
    getClientsTotal: (state) => state.clients.total,
    isClientsLoading: (state) => state.clients.loading,
    getClientsError: (state) => state.clients.error
  },

  mutations: {
    // Precios
    setItems: (state, payload) => {
      state.data = payload;
    },
    setTotalItems: (state, payload) => {
      state.total_data = payload;
    },
    setLoading: (state, payload) => {
      state.loading = payload;
    },
    setError: (state, payload) => {
      state.error = payload;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Rutas
    SET_RUTAS: (state, payload) => {
      state.rutas.data = payload;
    },
    SET_RUTAS_LOADING: (state, payload) => {
      state.rutas.loading = payload;
    },
    SET_RUTAS_ERROR: (state, payload) => {
      state.rutas.error = payload;
    },

    // Clientes
    SET_CLIENTS: (state, payload) => {
      state.clients.data = payload;
    },
    SET_CLIENTS_TOTAL: (state, payload) => {
      state.clients.total = payload;
    },
    SET_CLIENTS_LOADING: (state, payload) => {
      state.clients.loading = payload;
    },
    SET_CLIENTS_ERROR: (state, payload) => {
      state.clients.error = payload;
    }
  },

  actions: {
    // Precios - list
    list: async ({ commit, rootGetters }, { page = 1, rows = 10, search = '', order_by = 'Id', order_asc = true }) => {
      const custom_headers = rootGetters.getHeaders;
      commit('setLoading', true);
      commit('clearError');

      try {
        const response = await sendRequest(
          `administrator/heishop/precios/list?isDatatable=true&page=${page}&rows=${rows}&search=${search}&order_by=${order_by}&order_asc=${order_asc}`,
          'GET',
          null,
          null,
          null,
          custom_headers
        );

        if (response.data) {
          commit('setItems', response.data.list.result);
          commit('setTotalItems', response.data.list.total_rows);
        } else {
          commit('setError', {
            code: 'data_error',
            message: 'Error al procesar los datos recibidos',
            details: response.errors
          });
          console.error('Error al cargar los datos:', response.errors);
        }
      } catch (error) {
        commit('setError', {
          code: 'network_error',
          message: 'Error de conexión al cargar los precios',
          details: error.message
        });
        console.error('Error en la solicitud:', error);
      } finally {
        commit('setLoading', false);
      }
    },

    // Precios - add
    add: async ({ commit, dispatch, rootGetters }, { data }) => {
      const custom_headers = rootGetters.getHeaders;
      commit('setLoading', true);
      commit('clearError');
    
      try {
        // Validación de datos
        if (!data || Object.keys(data).length === 0) {
          const error = {
            code: 'invalid_data',
            message: 'Datos incompletos o vacíos para agregar precio.',
            details: data
          };
          commit('setError', error);
          return { success: false, error }; // No lanza, solo retorna
        }
    
        const response = await sendRequest(
          `administrator/heishop/precios/add`,
          'POST',
          null,
          data,
          null,
          custom_headers
        );
    
        if (response.success) {
          await dispatch('list', { page: 1, rows: 10 });
          return {
            success: true,
            data: response.data,
            message: 'Registro agregado correctamente.'
          };
        } else {
          const error = {
            code: response.errors?.[0]?.code || 'api_error',
            message: response.errors?.[0]?.message || 'Error desconocido del servidor',
            httpStatus: response.http,
            details: response.errors
          };
          commit('setError', error);
          return { success: false, error };
        }
    
      } catch (error) {
        const normalizedError = {
          code: error.code || 'network_error',
          message: error.message || 'Error de red al agregar precio',
          details: error.details || error
        };
        commit('setError', normalizedError);
        return { success: false, error: normalizedError };
      } finally {
        commit('setLoading', false);
      }
    },    

    // Rutas - fetchRutas
    fetchRutas: async ({ commit, rootGetters }) => {
      commit('SET_RUTAS_LOADING', true);
      commit('SET_RUTAS_ERROR', null);

      const custom_headers = rootGetters.getHeaders;

      try {
        const response = await sendRequest(
          `administrator/heishop/rutas`,
          'GET',
          null,
          null,
          null,
          custom_headers
        );

        if (!response?.data?.rutas) {
          throw new Error('Formato de respuesta inesperado');
        }

        const rutasMapped = Array.isArray(response.data.rutas)
          ? response.data.rutas
              .filter(ruta => ruta.ruta?.toLowerCase().startsWith('p'))
              .map(ruta => ({
                value: ruta.ruta || '',
                label: ruta.ruta || 'Ruta sin nombre',
                rawData: ruta,
              }))
          : [];

        commit('SET_RUTAS', rutasMapped);
        return rutasMapped;
      } catch (error) {
        commit('SET_RUTAS_ERROR', error.message || 'Error al cargar rutas');
        console.error('Error en fetchRutas:', error);
        throw error;
      } finally {
        commit('SET_RUTAS_LOADING', false);
      }
    },

    // Clientes - fetchClients
    fetchClients: async ({ commit, rootGetters }, { ruta = '' } = {}) => {
      commit('SET_CLIENTS_LOADING', true);
      commit('SET_CLIENTS_ERROR', null);

      const custom_headers = rootGetters.getHeaders;

      try {
        let url = 'administrator/heishop/clientes/list';
        if (ruta) {
          url += `?ruta=${encodeURIComponent(ruta)}`;
        }
        const response = await sendRequest(
          url,
          'GET',
          null,
          null,
          null,
          custom_headers
        );

        if (!response?.data || !response.data.list || !Array.isArray(response.data.list)) {
          throw new Error(response?.errors?.[0]?.message || 'Estructura de respuesta inválida');
        }

        const clientsMapped = response.data.list.map(client => ({
          value: client.ClaveFerguez || '',
          label: client.ClaveFerguez || 'Cliente sin clave',
          rawData: client,
        }));

        commit('SET_CLIENTS', clientsMapped);
        commit('SET_CLIENTS_TOTAL', response.data.list.total_rows || 0);

        return clientsMapped;
      } catch (error) {
        commit('SET_CLIENTS_ERROR', error.message || 'Error al cargar clientes');
        console.error('Error en fetchClients:', error);
        throw error;
      } finally {
        commit('SET_CLIENTS_LOADING', false);
      }
    }
  }
};
